import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getLatestTopicVersion,
  getPromptFragmentsByTopicVersion,
  getTopicVersionWithPrompts,
  getVersionsWithPrompts,
  createTopicVersion,
  updateTopic,
  exportPromptsByTopicVersions,
} from "../../services/topicService";
import exportService from "../../services/exportService";
import { skillService } from "../../services/skillService";
import {
  createTestConfig,
  updateTestConfig,
} from "../../services/testConfigService";
import { useConfirmation } from "../common/ConfirmationContext";
import PropertiesControl from "../common/PropertiesControl";
import { ResizableHorizontalDivider } from "../common/ResizableHorizontalDivider";
import { ResizableVerticalDivider } from "../common/ResizableVerticalDivider";
import { useToast } from "../common/ToastContext";
import EventTimelineView from "../sessions/EventTimelineView";
import PromptDiff from "./PromptDiff";
import PromptRibbon from "./PromptRibbon";
import {
  launchConfigurationTemplate,
  promptTemplate,
  validationConfigurationTemplate,
  voicemailTemplate,
} from "./prompts";
import PromptTabs from "./PromptTabs";
import PromptTree from "./PromptTree";
import ReleaseNoteModal from "./ReleaseNoteModal";
import TemplateTree from "./TemplateTree";
import { calculateTokenCount } from "../../utils/tokenUtil";
import { toTitleCase } from "../../utils/miscUtils";
import { toolsService } from "../../services/toolsService";
import promptFragmentsService from "../../services/promptFragmentsService";
import personaService from "../../services/personaService";
import { tagService } from "../../services/tagService";
import { currentUserHasRole } from "../../services/userService";
import { getTestConfigsByTopicKey } from "../../services/testConfigService";
import { generateConsolidatedPromptPreview } from "../../utils/promptPreview/generatePromptPreview";
import { useLoading } from "../common/LoadingContext";
import yaml from "js-yaml";

const MENU_DISABLED = { label: "Disabled", value: "disabled" };

const SUPPORTED_MODALITIES = [
  { label: "Voice", value: "voice" },
  { label: "Text", value: "text" },
];

const PYTHON_PROMPT_TYPE = { label: "Python", value: "python" };
const JINJA_TYPE = { label: "Jinja", value: "jinja" };
const TEXT_TYPE = { label: "Text", value: "text" };
const TRUE_TYPE = { label: "True", value: "true" };
const FALSE_TYPE = { label: "False", value: "false" };
const PREVIEW_PATH = "prompt/preview/preview";
const SCRIPT_PROPERTIES = "Script Properties";
const SCRIPT_PROPERTY_SUFFIXES = ["_skill", "_type", "_tools", "_skip_llm"];
const TOKEN_COUNT = "token_count";
const PROMPT_TYPE_MAPPER = {
  preprocessing: "pre-processing",
  primary: "primary",
  voicemail: "voicemail",
};

export const PRIORITY_LEVELS = [
  {
    label: "High",
    value: "high",
    tooltip: "Important and impacts project goals; should be prioritized soon.",
  },
  {
    label: "Medium",
    value: "medium",
    tooltip:
      "Standard priority; contributes to long-term goals and ongoing projects.",
  },
  {
    label: "Low",
    value: "low",
    tooltip:
      "Minimal impact; can be validated when there are no pressing tasks.",
  },
  {
    label: "Disabled",
    value: "disabled",
    tooltip: "This topic will not appear for Champion validation.",
  },
];

const PromptEditor = ({ item, onClose }) => {
  const promptKey = item?.key;
  const propertiesControlRef = useRef();
  const { openConfirmation } = useConfirmation();
  const { showToast } = useToast();
  const [contents, setContents] = useState({});
  const [diffView, setDiffView] = useState(null);
  const [promptProperties, setPromptProperties] = useState({});
  const [promptFragments, setPromptFragments] = useState([]);
  const [stateData, setStateData] = useState(null);
  const [selectedPath, setSelectedPath] = useState("prompt/primary/content");
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [topicTags, setTopicTags] = useState([]);
  const [versions, setVersions] = useState([]);
  const [visibleTabs, setVisibleTabs] = useState(["prompt/primary/content"]);
  const [isCurrentVersionSelected, setIsCurrentVersionSelected] =
    useState(true);
  const [selectedLaunchConfig, setSelectedLaunchConfig] = useState(null);
  const [skills, setSkills] = useState([]);
  const [propertiesHeight, setPropertiesHeight] = useState(50);
  const [promptTreeHeight, setPromptTreeHeight] = useState(66);
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(10);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(25);
  const [tokenCount, setTokenCount] = useState(0);
  const [showVersionCommentModal, setShowVersionCommentModal] = useState(false);
  const [toolsChecklist, setToolsChecklist] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [versionsWithIds, setVersionsWithIds] = useState([]);
  const [testConfigs, setTestConfigs] = useState([]);
  const [canUpdateValidationConfig, setCanUpdateValidationConfig] =
    useState(false);
  const [imported, setImported] = useState(false);
  const [allTools, setAllTools] = useState([]);
  const { setIsLoading } = useLoading();

  const promptType =
    window.location.href.indexOf("/topics") !== -1
      ? "topic"
      : window.location.href.indexOf("/simulants") !== -1
        ? "simulant"
        : "?";

  const fetchTags = async () => {
    try {
      const tags = await tagService.getAllTags();
      if (tags) {
        setTopicTags(tags);
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const fetchTools = async () => {
    const mcpTools = await toolsService.getAll();
    setAllTools(mcpTools);

    const toolsByTag = {};

    mcpTools.forEach((tool) => {
      const tags = tool.tags || [];

      const categories = tags.length > 0 ? tags : ["general"];
      const hasDefaultTag = tags.includes("default");

      // If tool has multiple tags and one is "default", only show under "default"
      const filteredCategories =
        hasDefaultTag && tags.length > 1 ? ["default"] : categories;

      filteredCategories.forEach((tag) => {
        if (!toolsByTag[tag]) {
          toolsByTag[tag] = [];
        }
        toolsByTag[tag].push({
          label: tool.name,
          value: tool.name,
          tooltip: tool.description,
          tags: tool.tags,
        });
      });
    });

    const formatToolsWithCategories = (toolsObj, stage) => {
      const result = [];

      Object.keys(toolsObj)
        .sort()
        .forEach((category) => {
          // Filter out tools with 'conversation' tag if stage is not primary
          const categoryItems = toolsObj[category]
            .filter((item) => {
              if (
                stage !== "primary" &&
                item.tags &&
                item.tags.includes("conversation")
              ) {
                return false;
              }
              return true;
            })
            .sort((a, b) => a.label.localeCompare(b.label));

          if (categoryItems.length > 0) {
            result.push({
              label: category,
              isCategory: true,
            });
            categoryItems.forEach((item) => {
              result.push(item);
            });
          }
        });
      return result;
    };

    const tools = {
      "pre-processing": formatToolsWithCategories(toolsByTag, "pre-processing"),
      primary: formatToolsWithCategories(toolsByTag, "primary"),
    };

    return tools;
  };

  useEffect(() => {
    const checkExportAccess = async () => {
      try {
        const hasEngineerAccess = await currentUserHasRole("prompt-engineer");
        setCanUpdateValidationConfig(hasEngineerAccess);
      } catch (error) {
        setCanUpdateValidationConfig(false);
      }
    };
    checkExportAccess();
  }, []);

  useEffect(() => {
    const loadLatestTopicVersion = async (key) => {
      if (!key) return null;
      try {
        const topic = await getLatestTopicVersion(key);
        setSelectedVersion(topic?.version || null);
      } catch (error) {
        console.error("Error fetching latest topic version:", error);
      }
    };
    fetchTags();
    loadLatestTopicVersion(promptKey);
  }, [promptKey]);

  useEffect(() => {
    const loadPreferences = () => {
      try {
        const leftWidth = Number.parseInt(
          localStorage.getItem("left-sidebar-width") ?? "10",
        );
        const rightWidth = Number.parseInt(
          localStorage.getItem("right-sidebar-width") ?? "25",
        );
        const treeHeight = Number.parseInt(
          localStorage.getItem("prompt-tree-height") ?? "66",
        );
        const propHeight = Number.parseInt(
          localStorage.getItem("properties-height") ?? "50",
        );
        setLeftSidebarWidth(leftWidth);
        setRightSidebarWidth(rightWidth);
        setPromptTreeHeight(treeHeight);
        setPropertiesHeight(propHeight);
      } catch (error) {
        console.error("Error loading preferences:", error);
      }
    };
    loadPreferences();
  }, []);

  useEffect(() => {
    const loadTestConfigs = async (key) => {
      if (!key) return null;
      try {
        const configs = await getTestConfigsByTopicKey(key);
        setTestConfigs(configs);
      } catch (error) {
        console.error("Error fetching test configs:", error);
      }
    };
    loadTestConfigs(promptKey);
  }, [promptKey]);

  const savePreferences = () => {
    try {
      localStorage.setItem("left-sidebar-width", leftSidebarWidth.toString());
      localStorage.setItem("right-sidebar-width", rightSidebarWidth.toString());
      localStorage.setItem("prompt-tree-height", promptTreeHeight.toString());
      localStorage.setItem("properties-height", propertiesHeight.toString());
    } catch (error) {
      console.error("Error saving preferences:", error);
    }
  };

  const debouncedSavePreferences = debounce(savePreferences, 500);

  useEffect(() => {
    debouncedSavePreferences();
  }, [leftSidebarWidth, rightSidebarWidth, promptTreeHeight, propertiesHeight]);

  useEffect(() => {
    setPromptProperties((prev) => ({
      ...prev,
      "Script Properties": {
        ...prev["Script Properties"],
        token_count: {
          value: tokenCount.toString(),
          readOnly: true,
          visible: selectedPath.startsWith("prompt/"),
        },
      },
    }));
  }, [tokenCount]);

  const baseFolders = [
    "prompt/pre-processing",
    "prompt/primary",
    "prompt/voicemail",
  ];

  function findFragmentsInProcessingBlocks(fragments, data) {
    const updatedData = JSON.parse(JSON.stringify(data));

    if (fragments) {
      fragments = fragments
        .map((fragment) => {
          const foundInPreProcessing = checkBlockExists(
            fragment.block_name,
            updatedData.prompts.filter(
              (p) => p.prompt_type === "preprocessing",
            ),
          );

          const foundInPrimary = checkBlockExists(
            fragment.block_name,
            updatedData.prompts.filter((p) => p.prompt_type === "primary"),
          );

          return {
            ...fragment,
            found_in_pre_processing: foundInPreProcessing,
            found_in_primary: foundInPrimary,
          };
        })
        .filter(
          (fragment) =>
            fragment.found_in_pre_processing || fragment.found_in_primary,
        );
    }

    return fragments;
  }

  function checkBlockExists(blockName, processingBlocks) {
    if (!Array.isArray(processingBlocks)) {
      return false;
    }

    const blockPattern = new RegExp(
      `{%\\s*include\\s+["']${blockName}["']\\s*(?:(?:with(?:out)?\\s+context|ignore\\s+missing)\\s*)*%}|` +
        `{%\\s*include\\s+\\[(?:[^\\]]*["']${blockName}["'][^\\]]*)\\]\\s*(?:(?:with(?:out)?\\s+context|ignore\\s+missing)\\s*)*%}`,
      "i",
    );

    return processingBlocks.some(
      (block) =>
        block.content &&
        typeof block.content === "string" &&
        blockPattern.test(block.content),
    );
  }

  const loadPrompt = useCallback(async () => {
    if (!promptKey || !selectedVersion) return;
    const topicPromptData = await getTopicVersionWithPrompts(
      promptKey,
      selectedVersion,
    );
    // Filter out any pre-existing postprocessing prompts
    topicPromptData.prompts = topicPromptData.prompts.filter(
      (prompt) => prompt.prompt_type !== "postprocessing",
    );
    if (!topicPromptData) return;

    setImported(topicPromptData.imported || false);

    const fragmentsWithContent = await getPromptFragmentsByTopicVersion(
      promptKey,
      selectedVersion,
    );

    const fetchedSkills = await skillService.list();
    setSkills(fetchedSkills);

    setPromptFragments(
      findFragmentsInProcessingBlocks(fragmentsWithContent, topicPromptData),
    );

    // Uncomment this out when we want to allow users to select Conversation Processing Jobs
    // const processing_jobs = await getAllConversationProcessingJobs();
    // if (!processing_jobs || processing_jobs.detail) processing_jobs = [];

    const updatedPreSkills = [
      MENU_DISABLED,
      ...fetchedSkills
        .filter((skill) => skill.preProcessing)
        .map((skill) => ({
          label: skill.key,
          value: skill.key,
          tooltip: skill.description,
          example: skill.example,
          overloads: skill.overloads,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
    const contents_ = {};
    const scriptProps = {};
    let commitVersion;
    const tcl = await fetchTools();
    setToolsChecklist(tcl);
    topicPromptData.prompts.forEach((prompt) => {
      const name = prompt.file_name;
      const promptType = PROMPT_TYPE_MAPPER[prompt.prompt_type];
      contents_[`prompt/${promptType}/${name}`] = prompt.content;
      scriptProps[`${name}_skill`] = {
        value: prompt.skill_name || MENU_DISABLED.value,
        menuItems: updatedPreSkills,
        readOnly: false,
        visible: false,
      };
      const fileType = prompt.file_type;
      scriptProps[`${name}_type`] = {
        value: fileType || JINJA_TYPE.value,
        menuItems: [PYTHON_PROMPT_TYPE, JINJA_TYPE, TEXT_TYPE],
        readOnly: false,
        visible: false, // for now not shown for primary content
      };
      const toolValue = prompt.tools.toString();
      // Only pre-select default tools for version 1 prompts
      const defaultToolValue =
        topicPromptData.version === 1 &&
        !toolValue &&
        !topicPromptData.version_comment.includes("Duplicated from")
          ? (tcl[promptType] || [])
              .filter(
                (item) => !item.isCategory && item.tags?.includes("default"),
              )
              .map((item) => item.value)
              .join(",")
          : toolValue;
      scriptProps[`${name}_tools`] = {
        value: defaultToolValue || "",
        checklistItems: JSON.parse(JSON.stringify(tcl[promptType] || [])),
        readOnly: false,
        visible: name === "content",
      };
      scriptProps[`${name}_skip_llm`] = {
        value: prompt.skip_llm.toString() || FALSE_TYPE.value,
        menuItems: [FALSE_TYPE, TRUE_TYPE],
        readOnly: false,
        visible: true,
      };
    });
    setContents(reorganizeContents(contents_));
    const lcn = getLaunchConfigNames();
    if (!selectedLaunchConfig && lcn.length > 0) {
      setSelectedLaunchConfig(lcn[0]);
    }
    refreshVersionList(topicPromptData.version);
    const props = {
      "Script Properties": {
        token_count: { value: tokenCount, readOnly: true, visible: false },
        ...scriptProps,
      },
      Properties: {
        name: {
          value: topicPromptData.name,
          readOnly: false,
        },
        description: {
          value: topicPromptData.description,
          readOnly: false,
        },
        tags: {
          value: topicPromptData.tags?.map((tag) => tag.name).join(", ") || "",
          checklistItems: topicTags.map((tag) => ({
            label: tag.name,
            value: tag.name,
          })),
          readOnly: false,
          tooltip: "Tags for this prompt.",
          visible: promptType === "topic",
        },
        supported_modalities: {
          value:
            topicPromptData?.supported_modalities.join(",") ??
            SUPPORTED_MODALITIES.map((m) => m.value).join(","),
          checklistItems: SUPPORTED_MODALITIES,
          readOnly: false,
          tooltip: "Supported modalities for this prompt.",
          visible: promptType === "topic",
        },
        conversation_processing: {
          value: topicPromptData?.processing_jobs.join(","),
          readOnly: true,
        },
        version: {
          value: topicPromptData.version.toString(),
          readOnly: true,
        },
        version_comment: {
          value:
            topicPromptData?.version_comment ?? commitVersion?.comment ?? "",
          readOnly: true,
          tooltip:
            "The comment at the time version was saved. Check Release Notes to see if this was adjusted post-save.",
        },
        version_visibility: {
          value: topicPromptData?.visibility.join(",") ?? "",
          readOnly: true,
          tooltip:
            "The visibility at the time version was saved. Check Release Notes to see if this was adjusted post-save.",
          visible: promptType === "topic",
        },
        created_by: {
          value: topicPromptData.created_by,
          readOnly: true,
        },
        created_at: {
          value: new Date(topicPromptData.created_at).toLocaleString(),
          readOnly: true,
        },
        updated_by: {
          value: topicPromptData.updated_by,
          readOnly: true,
        },
        updated_at: {
          value: new Date(topicPromptData.updated_at).toLocaleString(),
          readOnly: true,
        },
      },
    };
    setPromptProperties(props);
    setVisibleTabs([]);
    const primaryContentPath = Object.keys(contents_).find((key) =>
      key.startsWith("prompt/primary/"),
    );
    if (primaryContentPath) {
      setVisibleTabs([primaryContentPath]);
      setSelectedPath(primaryContentPath);
      const count = await calculateTokenCount(contents_[primaryContentPath]);
      setTokenCount(count);
    }
    setStateData({
      contents: JSON.parse(JSON.stringify(contents_)),
      properties: JSON.parse(JSON.stringify(props)),
    });
  }, [promptKey, selectedVersion, topicTags]);

  useEffect(() => {
    loadPrompt();
  }, [loadPrompt]);

  const hasUnsavedTopicChanges = useCallback((debug = false) => {
    if (!stateData) return false;

    const current = JSON.parse(JSON.stringify(promptProperties));
    const nameChange =
      current["Properties"].name.value !==
      stateData.properties.Properties.name.value;
    const descriptionChange =
      current["Properties"].description.value !==
      stateData.properties.Properties.description.value;
    const tagChange =
      current["Properties"].tags.value !==
      stateData.properties.Properties.tags.value;

    if (nameChange || descriptionChange || tagChange) {
      return true;
    }
    return false;
  });

  const hasUnsavedChanges = useCallback(() => {
    if (!stateData) return false;

    const sortedContents = JSON.parse(
      JSON.stringify(
        Object.keys(contents)
          .sort()
          .reduce((obj, key) => {
            obj[key] = contents[key];
            return obj;
          }, {}),
      ),
    );
    const sortedStateDataContents = JSON.parse(
      JSON.stringify(
        Object.keys(stateData.contents)
          .sort()
          .reduce((obj, key) => {
            obj[key] = stateData.contents[key];
            return obj;
          }, {}),
      ),
    );
    if (
      JSON.stringify(sortedContents) !== JSON.stringify(sortedStateDataContents)
    ) {
      return true;
    }

    const current = JSON.parse(JSON.stringify(promptProperties));
    const initial = stateData.properties;
    for (const category in current) {
      for (const key in current[category]) {
        if (key === "token_count") continue;
        // Skip name and description changes since handled by hasUnsavedTopicChanges
        if (
          category === "Properties" &&
          (key === "name" || key === "description" || key === "tags")
        ) {
          continue;
        }
        if (
          category === "Script Properties" &&
          (key.endsWith("_skill") || key.endsWith("_tools"))
        ) {
          if (current[category][key]?.value !== initial[category][key]?.value) {
            return true;
          }
        } else if (
          current[category][key]?.value !== initial[category][key]?.value
        ) {
          return true;
        }
      }
    }
    return false;
  }, [stateData, contents, promptProperties]);

  const handleCloseRequest = useCallback(() => {
    if (hasUnsavedChanges()) {
      openConfirmation({
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to close without saving?",
        onConfirm: onClose,
      });
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, openConfirmation, onClose]);

  const reorganizeContents = (inputDict) => {
    const preProcessingKeys = Object.fromEntries(
      Object.entries(inputDict).filter(([key]) =>
        key.startsWith("prompt/pre-processing"),
      ),
    );
    const primaryKeys = Object.fromEntries(
      Object.entries(inputDict).filter(([key]) =>
        key.startsWith("prompt/primary"),
      ),
    );
    const voicemailKeys = Object.fromEntries(
      Object.entries(inputDict).filter(([key]) =>
        key.startsWith("prompt/voicemail"),
      ),
    );
    return {
      ...preProcessingKeys,
      ...primaryKeys,
      ...voicemailKeys,
    };
  };

  const refreshVersionList = async (currentVersion) => {
    const versionsWithPrompts = await getVersionsWithPrompts(promptKey);
    setVersionsWithIds(versionsWithPrompts);
    // First element in resulting tuple is version number
    const versionNumbers = versionsWithPrompts.map((v) => v[0]);
    const sortedVersions = versionNumbers.sort((a, b) => b - a);
    const versionsList = sortedVersions.map((v) =>
      v === currentVersion ? `${v} (Current)` : v.toString(),
    );
    setVersions(versionsList);
    setIsCurrentVersionSelected(currentVersion === sortedVersions[0]);
    if (!versionsList.includes(selectedVersion.toString())) {
      setSelectedVersion(currentVersion);
    }
  };

  const cleanUpPreview = (path) => {
    const newContents = JSON.parse(JSON.stringify(contents));
    delete newContents[path];
    setContents(newContents);
    if (visibleTabs.includes(path)) {
      const newVisibleTabs = visibleTabs.filter((tab) => tab !== path);
      setVisibleTabs(newVisibleTabs);
      if (selectedPath === path) {
        setSelectedPath(newVisibleTabs[0] || "");
        handlePromptPropertyVisibility(newVisibleTabs[0]);
      }
    }
  };

  const handleOpenPreview = async () => {
    try {
      setIsLoading(true);
      if (isPreviewLoading) {
        showToast("Preview is currently loading");
        return;
      }

      if (!selectedLaunchConfig) {
        showToast("Select a config script");
        return;
      }

      if (selectedPath.includes(PREVIEW_PATH)) {
        showToast("Preview has already been generated");
        return;
      }

      setIsPreviewLoading(true);
      setSelectedPath(PREVIEW_PATH);
      setVisibleTabs((prevTabs) => [...prevTabs, PREVIEW_PATH]);

      const configFile = testConfigs.filter(
        (tc) => tc.name === selectedLaunchConfig,
      )[0];

      const generativeModel = await fetchPersonaModel(configFile);

      const previewData = await generatePreview(configFile, generativeModel);

      updatePreviewContent(PREVIEW_PATH, previewData.finalPreview);

      setTokenCount(previewData.adjustedTotalTokenCount);
      handlePreviewWarnings(
        previewData.unconfiguredVariablesCount,
        configFile?.persona,
        previewData.aiCallsSuccessful,
      );

      handlePromptPropertyVisibility(PREVIEW_PATH);
    } catch (error) {
      console.error("Error generating preview:", error);
      showToast("An error occurred while generating the preview.");
    } finally {
      setIsPreviewLoading(false);
      setIsLoading(false);
    }
  };

  const fetchPersonaModel = async (configFile) => {
    const configuredPersona = configFile?.persona;

    if (!configuredPersona) {
      showToast(
        "Warning: Persona is not defined in launch configuration. Cannot emulate LLM calls.",
      );
      return null;
    }

    try {
      const personas = await personaService.getAll();
      const matchedPersona = personas.find(
        (persona) => persona.name === configuredPersona,
      );

      if (matchedPersona?.models?.generative) {
        return matchedPersona.models.generative;
      }

      showToast(
        "Warning: Could not find generative model for the configured persona.",
      );
      return null;
    } catch (error) {
      console.error("Error fetching persona information:", error);
      showToast("Error: Could not fetch persona information.");
      return null;
    }
  };

  const handlePromptPropertyVisibility = (path) => {
    if (!path) return;

    Object.keys(promptProperties[SCRIPT_PROPERTIES]).forEach((key) => {
      if (
        key.endsWith("_skill") ||
        key.endsWith("_tools") ||
        key.endsWith("_type") ||
        key.endsWith("_skip_llm")
      ) {
        setPromptProperties((prev) => {
          const nextProperties = JSON.parse(JSON.stringify(prev));
          if (nextProperties[SCRIPT_PROPERTIES][key]) {
            nextProperties[SCRIPT_PROPERTIES][key].visible = false;
          }
          return nextProperties;
        });
      }
      if (key === "token_count") {
        setPromptProperties((prev) => {
          const nextProperties = JSON.parse(JSON.stringify(prev));
          if (nextProperties[SCRIPT_PROPERTIES][key]) {
            nextProperties[SCRIPT_PROPERTIES][key].visible =
              path.startsWith(PREVIEW_PATH);
          }
          return nextProperties;
        });
      }
    });

    const scriptName = path.split("/").pop();
    if (
      path.startsWith("prompt/pre-processing") ||
      path.startsWith("prompt/voicemail/")
    ) {
      const suffixes = ["skill", "type", "skip_llm"];
      for (const suffix of suffixes) {
        const actionPropertyName = `${scriptName}_${suffix}`;
        setPromptProperties((prev) => {
          const nextProperties = JSON.parse(JSON.stringify(prev));
          if (nextProperties[SCRIPT_PROPERTIES][actionPropertyName]) {
            nextProperties[SCRIPT_PROPERTIES][actionPropertyName].visible = true;
          }
          return nextProperties;
        });
      }
    }

    if (path.startsWith(PREVIEW_PATH)) {
      setPromptProperties((prev) => {
        const updatedProps = { ...prev };

        const updatedScriptProps = Object.fromEntries(
          Object.entries(updatedProps[SCRIPT_PROPERTIES] || {}).map(
            ([key, value]) => [
              key,
              key !== TOKEN_COUNT && value
                ? { ...value, visible: false }
                : value,
            ],
          ),
        );

        return {
          ...updatedProps,
          [SCRIPT_PROPERTIES]: updatedScriptProps,
        };
      });
    }

    if (path.startsWith("prompt/")) {
      const toolsPropName = `${scriptName}_tools`;
      setPromptProperties((prev) => {
        const nextProperties = JSON.parse(JSON.stringify(prev));
        if (nextProperties[SCRIPT_PROPERTIES][toolsPropName]) {
          const promptType = path.split("/")[1];
          const refreshedChecklistItems = JSON.parse(
            JSON.stringify(toolsChecklist[promptType] || []),
          );

          nextProperties[SCRIPT_PROPERTIES][toolsPropName] = {
            ...nextProperties[SCRIPT_PROPERTIES][toolsPropName],
            checklistItems: refreshedChecklistItems,
            visible: true,
          };
        }
        return nextProperties;
      });
    }
  };

  const generatePreview = async (configFile, generativeModel) => {
    return await generateConsolidatedPromptPreview({
      promptKey,
      selectedVersion,
      contents,
      configFile,
      promptFragments,
      getFragmentVersionsById: (id) =>
        promptFragmentsService.getFragmentVersionsById(id),
      calculateTokenCount,
      promptProperties,
      generativeModel,
    });
  };

  const updatePreviewContent = (previewPath, finalPreview) => {
    setContents((prevContents) => ({
      ...prevContents,
      [previewPath]: finalPreview,
    }));
  };

  const handlePreviewWarnings = (
    unconfiguredVariablesCount,
    configuredPersona,
    aiCallsSuccessful,
  ) => {
    if (configuredPersona && !aiCallsSuccessful) {
      showToast("Warning: Failed to process pre-processing prompts with AI.");
    }
  };

  const handleTreeSelect = async (path) => {
    if (selectedPath.includes(PREVIEW_PATH)) {
      cleanUpPreview(selectedPath);
    }
    setSelectedPath(path);
    if (!visibleTabs.includes(path)) {
      setVisibleTabs((prevTabs) => [...prevTabs, path]);
    } else {
      setSelectedPath(path);
    }

    handlePromptPropertyVisibility(path);
  };

  const handleHideTab = (path) => {
    if (selectedPath.includes(PREVIEW_PATH)) {
      cleanUpPreview(selectedPath);
      return;
    }
    const newVisibleTabs = visibleTabs.filter((tab) => tab !== path);
    setVisibleTabs(newVisibleTabs);
    if (selectedPath === path) {
      setSelectedPath(newVisibleTabs[0] || "");
      handlePromptPropertyVisibility(newVisibleTabs[0]);
    }
  };

  const handleContentChange = async (path, newContent) => {
    setContents((prevContents) => ({
      ...prevContents,
      [path]: newContent,
    }));
    if (path === selectedPath) {
      const count = await calculateTokenCount(newContent);
      setTokenCount(count);
    }
  };

  const handleAddFragment = (fragment) => {
    if (promptFragments) {
      const existingFragmentIndex = promptFragments.findIndex(
        (f) => f.block_name === fragment.block_name,
      );

      if (existingFragmentIndex !== -1) {
        const existingFragment = promptFragments[existingFragmentIndex];

        if (existingFragment.version !== fragment.version) {
          const updatedFragments = [...promptFragments];
          updatedFragments[existingFragmentIndex] = {
            ...fragment,
            is_fragment_changed: 2,
            is_version_changed: true,
            previous_version: existingFragment.version,
          };
          setPromptFragments(updatedFragments);
        }
      } else {
        setPromptFragments([
          ...promptFragments,
          { ...fragment, is_fragment_changed: 1 },
        ]);
      }
    } else {
      setPromptFragments([{ ...fragment, is_fragment_changed: 1 }]);
    }
  };

  const handleTreeDelete = (path) => {
    openConfirmation({
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this item? This action cannot be undone.",
      onConfirm: () => {
        const newContents = JSON.parse(JSON.stringify(contents));
        delete newContents[path];
        setContents(newContents);
        if (visibleTabs.includes(path)) {
          handleHideTab(path);
        }
        if (selectedPath === path) {
          if (visibleTabs.length > 0) {
            setSelectedPath(visibleTabs[0]);
            handleTreeSelect(visibleTabs[0]);
          } else {
            setSelectedPath("");
          }
        }
      },
    });
  };

  const handlePropertyChange = (category, key, value) => {
    setPromptProperties((prev) => {
      const nextProperties = JSON.parse(JSON.stringify(prev));
      if (nextProperties && nextProperties[category]) {
        if (
          (category === "Script Properties" &&
            (key.endsWith("_skill") ||
              key.endsWith("_type") ||
              key.endsWith("_tools"))) ||
          key.endsWith("_skip_llm")
        ) {
          nextProperties[category][key] = {
            ...nextProperties[category][key],
            value: value,
            visible: true,
          };
        } else if (nextProperties[category][key]) {
          nextProperties[category][key] = {
            ...nextProperties[category][key],
            value: value,
            visible: true,
          };
        }
      }
      return nextProperties;
    });
  };

  const isNameUnique = (name, currentPath = null) => {
    return !Object.keys(contents).some((path) => {
      const existingName = path.split("/").pop();
      return existingName === name && path !== currentPath;
    });
  };

  const voicemailExists = () => {
    return Object.keys(contents).some((path) =>
      path.startsWith("prompt/voicemail/"),
    );
  };

  const handleTreeAdd = (newPath) => {
    const name = newPath.split("/").pop();
    const newPathType = newPath.split("/")[1];
    if (!isNameUnique(name)) {
      showToast("An item with that name already exists", "error");
      return null;
    }
    if (voicemailExists() && newPathType === "voicemail") {
      showToast("A voicemail script already exists", "error");
      return null;
    }

    let comment;
    if (newPath.startsWith("prompt/voicemail")) {
      comment = voicemailTemplate;
    } else {
      comment = promptTemplate.replace("PROMPT_NAME", name);
    }

    setContents((prevContents) =>
      reorganizeContents({
        ...prevContents,
        [newPath]: comment,
      }),
    );
    if (
      comment === launchConfigurationTemplate ||
      comment === validationConfigurationTemplate
    ) {
      return newPath;
    }
    setPromptProperties((prev) => {
      const nextProperties = JSON.parse(JSON.stringify(prev));
      if (nextProperties["Script Properties"]) {
        const updatedPreSkills = [
          MENU_DISABLED,
          ...skills
            .filter((skill) => skill.preProcessing)
            .map((skill) => ({
              label: skill.key,
              value: skill.key,
              tooltip: skill.description,
              example: skill.example,
              overloads: skill.overloads,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ];

        nextProperties["Script Properties"][`${name}_skill`] = {
          value: MENU_DISABLED.value,
          menuItems: updatedPreSkills,
          readOnly: false,
          visible: true,
        };
        nextProperties["Script Properties"][`${name}_type`] = {
          value: JINJA_TYPE.value,
          menuItems: [JINJA_TYPE, PYTHON_PROMPT_TYPE, TEXT_TYPE],
          readOnly: false,
          visible: false, // name === "content", <- for now, we aren't showing this on content scripts
        };
        nextProperties["Script Properties"][`${name}_tools`] = {
          value: "",
          checklistItems: JSON.parse(
            JSON.stringify(toolsChecklist[newPathType] || []),
          ),
          readOnly: false,
          visible: true,
        };
        nextProperties["Script Properties"][`${name}_skip_llm`] = {
          value: newPath.startsWith("prompt/voicemail")
            ? TRUE_TYPE.value
            : FALSE_TYPE.value,
          menuItems: [FALSE_TYPE, TRUE_TYPE],
          readOnly: false,
          visible: false, // name === "content", <- for now, we aren't showing this on content scripts
        };
      }
      return nextProperties;
    });
    return newPath;
  };

  const handleTreeRename = (oldPath, newPath) => {
    const newName = newPath.split("/").pop();
    const oldName = oldPath.split("/").pop();
    if (!isNameUnique(newName, oldPath)) {
      showToast("An item with that name already exists", "error");
      return null;
    }
    setContents((prevContents) => {
      const newContents = JSON.parse(JSON.stringify(prevContents));
      newContents[newPath] = newContents[oldPath];
      delete newContents[oldPath];
      return reorganizeContents(newContents);
    });
    // Script properties are keyed by script name, so they must be re-keyed too or
    // buildPromptData loses them and sends file_type: null.
    setPromptProperties((prev) => {
      const nextProperties = JSON.parse(JSON.stringify(prev));
      const scriptProps = nextProperties[SCRIPT_PROPERTIES];
      if (!scriptProps) return nextProperties;
      nextProperties[SCRIPT_PROPERTIES] = Object.fromEntries(
        Object.entries(scriptProps).map(([key, value]) => {
          const suffix = SCRIPT_PROPERTY_SUFFIXES.find(
            (s) => key === `${oldName}${s}`,
          );
          return suffix ? [`${newName}${suffix}`, value] : [key, value];
        }),
      );
      return nextProperties;
    });
    setVisibleTabs((prevTabs) => prevTabs.filter((tab) => tab !== oldPath));
    return newPath;
  };

  const moveItem = (obj, key, direction) => {
    const keys = Object.keys(obj);
    const index = keys.indexOf(key);
    if (
      (direction === "up" && index <= 0) ||
      (direction === "down" && index === keys.length - 1)
    ) {
      return obj;
    }
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [keys[index], keys[newIndex]] = [keys[newIndex], keys[index]];
    return Object.fromEntries(keys.map((k) => [k, obj[k]]));
  };

  const handleTreeMove = (path, direction) => {
    setContents((prevContents) => moveItem(prevContents, path, direction));
  };

  const parseLaunchConfigContent = (name, content) => {
    try {
      const parsedYaml = yaml.load(content);
      return {
        name: name,
        persona: parsedYaml.persona,
        subject: parsedYaml.subject || "",
        modality:
          parsedYaml.modality || parsedYaml.variables?.modality || "voice", // default to voice for older configs
        variables: parsedYaml.variables,
      };
    } catch (error) {
      showToast("Error parsing launch configuration content", "error");
      return null;
    }
  };

  const handleAddNewLaunchConfig = async (name, type, content = null) => {
    if (type === "validation" && !canUpdateValidationConfig) {
      showToast(
        "You do not have permission to add validation configurations",
        "error",
      );
      return testConfigs;
    }
    const existingNames = testConfigs.map((config) => config.name);
    if (existingNames.includes(name)) {
      showToast("A test configuration with that name already exists", "error");
      return testConfigs;
    }
    if (!content) {
      if (type === "validation") {
        content = validationConfigurationTemplate;
      } else {
        content = launchConfigurationTemplate;
      }
    }

    const parsedContent = parseLaunchConfigContent(name, content);
    if (!parsedContent) {
      return;
    }
    let newConfig;
    try {
      newConfig = await createTestConfig(promptKey, parsedContent);
    } catch (error) {
      showToast(
        `Failed to create launch configuration: ${error.message}`,
        "error",
      );
      return testConfigs;
    }

    const updatedConfigs = [...testConfigs, newConfig];
    setSelectedLaunchConfig(name);
    setTestConfigs(updatedConfigs);
    showToast(`Launch configuration '${name}' added`, "info", 1000);
    return updatedConfigs;
  };

  const handleSaveLaunchConfig = async (id, name, content) => {
    if (name === "validation" && !canUpdateValidationConfig) {
      showToast(
        "You do not have permission to update validation configurations",
        "error",
      );
      return testConfigs;
    }
    const configData = parseLaunchConfigContent(name, content);
    let updatedConfigs = testConfigs;
    updatedConfigs = testConfigs.map((config) =>
      config.id === id ? { ...config, ...configData } : config,
    );
    setSelectedLaunchConfig(name);
    setTestConfigs(updatedConfigs);
    if (!configData) {
      return testConfigs;
    }
    try {
      await updateTestConfig({
        id: id,
        data: configData,
      });
      showToast(`Launch configuration saved`, "info", 1000);
      return updatedConfigs;
    } catch (error) {
      showToast(`Failed to save launch configuration`, "error");
      return testConfigs;
    }
  };

  const handleSavePrompt = async (debug = false) => {
    if (selectedPath.includes(PREVIEW_PATH)) {
      showToast("Please exit the prompt preview before saving.");
      return;
    }
    const hasFragmentChange = await compareContentWithFragments();
    if (
      !hasUnsavedChanges() &&
      !hasUnsavedTopicChanges(debug) &&
      !hasFragmentChange
    ) {
      showToast("No changes to save", "info", 1000);
      return;
    }
    if (hasFragmentChange instanceof Error) {
      return;
    }
    const supportedModalities =
      promptProperties.Properties?.supported_modalities?.value;
    if (!supportedModalities || supportedModalities.length === 0) {
      showToast(`At least one supported modality must be selected`, "error");
      return;
    }
    // Validate tools by supported modalities
    for (const path of Object.keys(contents)) {
      const scriptName = path.split("/").pop();
      const toolsPropName = `${scriptName}_tools`;
      const toolsValue =
        promptProperties[SCRIPT_PROPERTIES][toolsPropName]?.value || "";
      const tools = allTools.filter((tool) =>
        toolsValue.split(",").includes(tool.name),
      );
      if (!validateToolsByModality(tools, supportedModalities, scriptName)) {
        return;
      }
    }

    if (hasUnsavedTopicChanges(debug)) {
      const tagValues = promptProperties.Properties.tags.value
        .split(",")
        .map((tag) => tag.trim());
      const selectedTags = topicTags.filter((tag) =>
        tagValues.includes(tag.name),
      );
      try {
        await updateTopic({
          key: promptKey,
          name: promptProperties.Properties.name.value,
          description: promptProperties.Properties.description.value,
          tags: selectedTags,
          test_configs: testConfigs,
        });
        showToast(`Updated topic properties`, "info", 1000);
      } catch (error) {
        showToast(`Failed to update topic properties`, "error");
      }
    }
    if (hasUnsavedChanges() || hasFragmentChange) {
      setShowVersionCommentModal(true);
    }
  };

  const validateToolsByModality = (tools, supportedModalities, scriptName) => {
    let validationFailed = false;
    tools.forEach((tool) => {
      if (supportedModalities === "text" && tool?.tags.includes("voice-only")) {
        showToast(
          `Incompatible tool selection for script '${scriptName}'. Voice only '${tool.name}' tool cannot be used when only 'text' modality is supported.`,
          "error",
        );
        validationFailed = true;
        return false;
      }
      if (supportedModalities === "voice" && tool?.tags.includes("text-only")) {
        showToast(
          `Incompatible tool selection for script '${scriptName}'. Text only '${tool.name}' tool cannot be used when only 'voice' modality is supported.`,
          "error",
        );
        validationFailed = true;
        return false;
      }
    });

    return !validationFailed;
  };

  const extractContentByPrefix = (prefix) => {
    return Object.entries(contents).reduce((acc, [key, value]) => {
      if (key.startsWith(prefix)) {
        return acc + (value || "");
      }
      return acc;
    }, "");
  };

  const extractBlockNames = (content) => {
    const regex =
      /{%\s*include\s+(?:\[((?:["'][^"']+["'](?:\s*,\s*["'][^"']+["'])*))]\s*|["']([^"']+)["']|(\w+(?:\.\w+)*))\s*(?:(?:with(?:out)?\s+context|ignore\s+missing)\s*)*%}/g;
    const blockNames = [];
    let match;

    while ((match = regex.exec(content)) !== null) {
      const [fullMatch, arrayMatch, quotedMatch, variableMatch] = match;

      if (arrayMatch) {
        const arrayItems = arrayMatch
          .split(",")
          .map((item) => item.trim().replace(/^["']|["']$/g, ""));

        blockNames.push(...arrayItems);
      } else if (quotedMatch) {
        blockNames.push(quotedMatch);
      } else if (variableMatch) {
        blockNames.push(variableMatch);
      }
    }

    return blockNames;
  };

  const getUniqueBlockNames = (preProcessingContent, primaryContent) => {
    const contentBlockNames = [
      ...extractBlockNames(preProcessingContent),
      ...extractBlockNames(primaryContent),
    ];
    return [...new Set(contentBlockNames)];
  };

  const updateExistingFragments = (fragments, uniqueContentBlocks) => {
    if (!fragments) return [];
    const filteredFragments = fragments.filter((fragment) => {
      if (
        fragment.is_fragment_changed === 1 &&
        !uniqueContentBlocks.includes(fragment.block_name)
      ) {
        return false;
      }
      return true;
    });

    return filteredFragments.map((fragment) => {
      if (!uniqueContentBlocks.includes(fragment.block_name)) {
        return { ...fragment, is_fragment_changed: -1 };
      } else if (fragment.is_fragment_changed === -1) {
        return { ...fragment, is_fragment_changed: 0 };
      } else {
        return { ...fragment };
      }
    });
  };

  const fetchNewFragments = async (uniqueContentBlocks, fragmentBlockNames) => {
    const newBlockNames = uniqueContentBlocks.filter(
      (blockName) => !fragmentBlockNames.includes(blockName),
    );

    if (newBlockNames.length === 0) {
      return newBlockNames;
    }

    const newFragmentPromises = newBlockNames.map(async (blockName) => {
      try {
        const allFragments = await promptFragmentsService.getFragments();
        const matchingFragment = allFragments.find(
          (f) => f.block_name === blockName,
        );

        if (matchingFragment) {
          const latestVersion =
            await promptFragmentsService.getLatestFragmentVersion(
              matchingFragment.id,
            );

          return {
            block_name: blockName,
            prompt_fragment_id: matchingFragment.id,
            version: latestVersion,
            is_fragment_changed: 1,
          };
        }
        throw new Error(`Failed to fetch fragment data for ${blockName}`);
      } catch (error) {
        showToast(
          `Failed to fetch fragment data for ${blockName}`,
          "error",
          5000,
        );
        return null;
      }
    });

    const newMatchedFragments = await Promise.all(newFragmentPromises);
    const fragmentsHasNull = newMatchedFragments.some((e) => e === null);

    if (fragmentsHasNull) {
      return new Error(`Error fetching new fragments.`);
    }

    return newMatchedFragments;
  };

  const hasFragmentChanges = (fragments) => {
    return fragments.some(
      (item) =>
        item.is_fragment_changed === true || item.is_version_changed === true,
    );
  };

  const compareContentWithFragments = async () => {
    try {
      const preProcessingContent = extractContentByPrefix(
        "prompt/pre-processing/",
      );
      const primaryContent = contents["prompt/primary/content"] || "";

      const uniqueContentBlocks = getUniqueBlockNames(
        preProcessingContent,
        primaryContent,
      );

      const updatedFragments = updateExistingFragments(
        promptFragments,
        uniqueContentBlocks,
      );

      const fragmentBlockNames = updatedFragments.map((f) => f.block_name);

      const newFragments = await fetchNewFragments(
        uniqueContentBlocks,
        fragmentBlockNames,
      );

      if (newFragments instanceof Error) {
        throw new Error("Fragment not found.");
      }

      const finalFragments = [...updatedFragments, ...newFragments];
      setPromptFragments(finalFragments);

      return hasFragmentChanges(finalFragments);
    } catch (error) {
      return new Error("Error comparing content with fragments.");
    }
  };

  const buildPromptData = (prompts, promptType, startIdx) => {
    let promptsData = [];
    let idx = startIdx;
    Object.entries(prompts).forEach(([key, content]) => {
      const scriptName = key.split("/").pop();
      const toolsValue =
        promptProperties["Script Properties"][`${scriptName}_tools`]?.value ||
        "";
      const toolsArray = toolsValue
        .split(",")
        .map((tool) => tool.trim())
        .filter((tool) => tool.length > 0);

      const enabledTools = toolsArray;

      const value = {
        file_name: scriptName,
        file_type:
          promptProperties["Script Properties"][`${scriptName}_type`]?.value ||
          null,
        content: content,
        prompt_type: promptType,
        order_index: idx,
        skill_name:
          promptProperties["Script Properties"][`${scriptName}_skill`]?.value ||
          null,
        skip_llm:
          promptProperties["Script Properties"][`${scriptName}_skip_llm`]
            ?.value === TRUE_TYPE.value,
        tools: enabledTools,
      };
      promptsData.push(value);
      if (idx) {
        idx += 1;
      }
    });
    return promptsData;
  };

  const handleSaveWithComment = async (data) => {
    const comment = data.comment;
    const visibility = data.visibility;
    const preprocessing = Object.fromEntries(
      Object.entries(contents).filter(([key, _]) =>
        key.startsWith("prompt/pre-processing"),
      ),
    );
    const primary = Object.fromEntries(
      Object.entries(contents).filter(([key, _]) =>
        key.startsWith("prompt/primary"),
      ),
    );
    const voicemail = Object.fromEntries(
      Object.entries(contents).filter(([key, _]) =>
        key.startsWith("prompt/voicemail"),
      ),
    );

    const prompts = [
      ...buildPromptData(
        preprocessing,
        "preprocessing",
        -Object.keys(preprocessing).length,
      ),
      ...buildPromptData(primary, "primary", 0),
      ...buildPromptData(voicemail, "voicemail", null),
    ];

    const filteredFragments = promptFragments.filter(
      (fragment) => fragment.is_fragment_changed !== -1,
    );

    const cleanedFragments = filteredFragments.map(
      ({
        is_fragment_changed,
        is_version_changed,
        previous_version,
        ...fragment
      }) => fragment,
    );

    setPromptFragments(cleanedFragments);

    try {
      const topicVersionResult = await createTopicVersion({
        topic_key: promptKey,
        version_comment: comment,
        visibility: visibility,
        supported_modalities:
          promptProperties.Properties.supported_modalities.value
            .split(",")
            .map((m) => m.trim()) || [],
        fragments: cleanedFragments,
        prompts: prompts,
      });

      setPromptProperties((prev) => ({
        ...prev,
        Properties: {
          ...prev.Properties,
          version: {
            value: topicVersionResult.version.toString(),
            readOnly: true,
          },
          version_comment: {
            value: comment,
            readOnly: true,
            tooltip:
              "The comment at the time version was saved. Check Release Notes to see if this was adjusted post-save.",
          },
          version_visibility: {
            value: visibility.join(","),
            readOnly: true,
            tooltip:
              "The visibility at the time version was saved. Check Release Notes to see if this was adjusted post-save.",
          },
          updated_by: { value: topicVersionResult.updated_by, readOnly: true },
          updated_at: {
            value: new Date(topicVersionResult.updated_at).toLocaleString(),
            readOnly: true,
          },
        },
      }));
      const prevSelectedPath = selectedPath;
      const prevOpenPaths = visibleTabs;
      setSelectedVersion(topicVersionResult.version);
      await refreshVersionList(topicVersionResult.version);
      setTimeout(() => {
        prevOpenPaths.forEach((path) => {
          window.dispatchEvent(
            new CustomEvent("emulateTreeMenuClick", { detail: { path } }),
          );
        });
        setSelectedPath(prevSelectedPath);
      }, 500);
      setStateData({
        contents: JSON.parse(JSON.stringify(contents)),
        properties: JSON.parse(JSON.stringify(promptProperties)),
      });
      window.dispatchEvent(new CustomEvent("refreshReleaseNotes", {}));
      window.dispatchEvent(new CustomEvent("refreshPromptTabs", {}));
      showToast(`Saved version ${topicVersionResult.version}`, "info", 1000);
      setShowVersionCommentModal(false);
    } catch (error) {
      if (error?.response?.status === 403 || error?.status === 403) {
        showToast(
          "You don't have permission to save this topic. Please request access from an administrator.",
          "error",
          5000,
        );

        console.error(`Authorization failed when saving:`, error);
      } else {
        showToast(
          "Failed to save topic: " + (error.message || "Unknown error"),
          "error",
          5000,
        );
      }
    }
  };

  const handleDeletePrompt = () => {
    updateTopic({
      key: promptKey,
      name: promptProperties.Properties?.name?.value || item?.name,
      description:
        promptProperties.Properties?.description?.value || item?.description,
      is_deleted: true,
    });
    onClose();
  };

  const handleVersionSelect = async (version) => {
    const newVersion = parseInt(version.split(" ")[0]);
    // In diff mode, retarget the left/current side instead of leaving the diff stale.
    if (diffView) {
      if (newVersion === diffView.right) {
        showToast("That version is already being compared against", "info");
        return;
      }
      const versionData = await getTopicVersionWithPrompts(
        promptKey,
        newVersion,
      );
      if (!versionData) {
        showToast("Error loading version", "error");
        return;
      }
      const versionContents = {};
      versionData.prompts.forEach((prompt) => {
        const type = PROMPT_TYPE_MAPPER[prompt.prompt_type];
        const name = prompt.file_name;
        if (type && name) {
          versionContents[`prompt/${type}/${name}`] = prompt.content;
        }
      });
      const newOriginal = versionContents[diffView.path];
      if (newOriginal === undefined) {
        showToast(
          "The path selected is not available in the selected version",
          "info",
        );
        return;
      }
      setDiffView((prev) => ({
        ...prev,
        original: newOriginal,
        left: newVersion,
      }));
      setSelectedVersion(newVersion);
      return;
    }
    setSelectedVersion(newVersion);
  };

  const handleShowComparison = async (path, comparisonVersion) => {
    const comparisonData = await getTopicVersionWithPrompts(
      promptKey,
      comparisonVersion,
    );
    if (!comparisonData) {
      showToast("Error loading comparison version", "error");
      return;
    }
    const comparisonContents = {};
    comparisonData.prompts.forEach((prompt) => {
      const type = PROMPT_TYPE_MAPPER[prompt.prompt_type];
      const name = prompt.file_name;
      if (type && name) {
        comparisonContents[`prompt/${type}/${name}`] = prompt.content;
      }
    });
    const comparisonContent = comparisonContents[path];
    if (!comparisonContent) {
      showToast(
        "The path selected is not available in the comparison version",
        "info",
      );
      return;
    }
    setDiffView({
      path,
      original: contents[path],
      modified: comparisonContent,
      left: selectedVersion,
      right: comparisonVersion,
    });
  };

  const handleCloseDiff = () => {
    setDiffView(null);
  };

  const handleExport = async (topicVersions) => {
    try {
      const responses = await exportPromptsByTopicVersions(topicVersions);
      let failedExports = [];
      for (const response of responses) {
        const fileName = response?.file_name;
        const fileData = response?.file_content;
        if (!fileName || !fileData) {
          showToast("Failed to export fragment", "error");
          throw new Error("Invalid file data received");
        }

        const success = await exportService.getJsonExport({
          fileName: fileName,
          jsonData: fileData,
        });
        if (!success) {
          failedExports.push(fileName);
        }
      }
      if (failedExports.length > 0) {
        showToast(
          `Failed to export fragments: ${failedExports.join(", ")}`,
          "error",
          5000,
        );
      }
    } catch (error) {
      showToast("Failed to export topics", "error", 5000);
    }
  };

  const getLaunchConfigs = () => {
    return testConfigs.reduce((acc, config) => {
      acc[config.name] = config;
      return acc;
    }, {});
  };

  const getLaunchConfigNames = () => {
    const launchConfigs = getLaunchConfigs();
    const names = Object.keys(launchConfigs).map((key) => key.split("/").pop());
    return names.sort();
  };

  const getSelectedPromptType = () => {
    const scriptProps = promptProperties["Script Properties"] || {};
    const name = selectedPath.split("/").pop();
    const type = scriptProps[`${name}_type`] || {};
    if (name.includes("preview")) return "markdown";
    const result = type?.value === "python" ? "python" : null; // return null if not python
    return result;
  };

  return (
    <div>
      <div className="fixed left-2 top-[70px] bottom-0 flex">
        <div
          style={{ width: `${leftSidebarWidth}vw` }}
          className="flex flex-col"
        >
          <div style={{ height: `${promptTreeHeight}%` }}>
            <PromptTree
              data={Object.keys(contents)}
              initiallyExpanded={false}
              baseFolders={baseFolders}
              selectedPath={selectedPath}
              onSelect={handleTreeSelect}
              onDeletePrompt={handleTreeDelete}
              onAddPrompt={handleTreeAdd}
              onRenamePrompt={handleTreeRename}
              onMovePrompt={handleTreeMove}
              readOnly={!isCurrentVersionSelected || imported}
              selectedVersion={selectedVersion}
              versions={versions}
              onCompare={handleShowComparison}
              promptType={promptType}
            />
          </div>
          <ResizableHorizontalDivider
            minSize={10}
            maxSize={90}
            onDoubleClick={() => {
              setPromptTreeHeight(66);
            }}
            onResize={setPromptTreeHeight}
          />
          <div style={{ height: `${100 - promptTreeHeight}%` }}>
            <TemplateTree />
          </div>
        </div>
        <ResizableVerticalDivider
          minSize={4.5}
          maxSize={30}
          onResize={setLeftSidebarWidth}
          onDoubleClick={() => setLeftSidebarWidth(10)}
        />
      </div>
      <div
        className="fixed top-[19px] bottom-0 overflow-hidden z-1"
        style={{
          left: `calc(${leftSidebarWidth}vw + 10px)`,
          width: `calc(100vw - ${leftSidebarWidth}vw - ${rightSidebarWidth}vw - 12px)`,
        }}
      >
        <div className="h-[51px]">
          <PromptRibbon
            promptKey={promptKey}
            promptName={promptProperties.Properties?.name?.value || ""}
            onSave={handleSavePrompt}
            onPreview={handleOpenPreview}
            onClose={handleCloseRequest}
            onDelete={handleDeletePrompt}
            versions={versions}
            onSelectVersion={handleVersionSelect}
            isCurrentVersion={isCurrentVersionSelected}
            selectedVersion={selectedVersion}
            comparisonVersion={diffView?.right}
            launchConfigs={getLaunchConfigNames().filter(
              (name) => name !== "validation",
            )} // Filter out validation config since that shouldn't be run in debug
            launchConfigContents={getLaunchConfigs()}
            selectedLaunchConfig={selectedLaunchConfig}
            onSelectLaunchConfig={(config) => setSelectedLaunchConfig(config)}
            topicVersions={versionsWithIds}
            onExport={handleExport}
          />
        </div>
        {diffView ? (
          <PromptDiff
            data={diffView}
            readOnly={true}
            onClose={handleCloseDiff}
          />
        ) : visibleTabs.length > 0 ? (
          <PromptTabs
            initialCode={contents}
            selectedPath={selectedPath}
            visibleTabs={visibleTabs}
            onSelectPath={handleTreeSelect}
            onHideTab={handleHideTab}
            onContentChange={handleContentChange}
            onFragmentAdd={handleAddFragment}
            readOnly={!isCurrentVersionSelected || imported}
            promptKey={promptKey}
            promptType={promptType}
            language={getSelectedPromptType()}
            promptFragments={promptFragments}
            isPreviewLoading={isPreviewLoading}
            testConfigs={testConfigs}
            onTestConfigUpdate={handleSaveLaunchConfig}
            onTestConfigAdd={handleAddNewLaunchConfig}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            Click on a prompt in the tree to edit its content
          </div>
        )}
      </div>
      <div className="fixed right-0 top-[70px] bottom-20 flex">
        <ResizableVerticalDivider
          minSize={60}
          maxSize={90}
          onResize={(width) => setRightSidebarWidth(100 - width)}
          onDoubleClick={() => setRightSidebarWidth(25)}
        />
        <div
          style={{ width: `${rightSidebarWidth}vw` }}
          className="flex flex-col"
        >
          <div
            className="dark-slim-scrollbar overflow-y-auto"
            style={{ height: `${propertiesHeight}%` }}
          >
            <PropertiesControl
              ref={propertiesControlRef}
              properties={promptProperties}
              onPropertyChange={handlePropertyChange}
              initiallyExpanded={true}
              readOnly={!isCurrentVersionSelected}
              categoryAliases={{
                Properties: `${toTitleCase(promptType)} Properties`,
              }}
            />
          </div>
          <ResizableHorizontalDivider
            minSize={10}
            maxSize={90}
            onDoubleClick={() => {
              setPropertiesHeight(50);
            }}
            onResize={setPropertiesHeight}
          />
          <div
            className="backdrop-blur-lg"
            style={{ height: `${100 - propertiesHeight}%` }}
          >
            <EventTimelineView />
          </div>
        </div>
      </div>
      {showVersionCommentModal && (
        <ReleaseNoteModal
          initialVisibilities={
            promptProperties?.Properties?.version_visibility.value.split(",") ||
            []
          }
          onSave={handleSaveWithComment}
          onClose={() => setShowVersionCommentModal(false)}
          showVisibility={promptType === "topic"}
          fragments={promptFragments || []}
        />
      )}
    </div>
  );
};

export default PromptEditor;
